import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TemplateContextValue, TemplateId } from '@/types/template';
import { getAllTemplates, isValidTemplateId } from '@/config/templateRegistry';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from './TenantContext';
import { useToast } from '@/hooks/use-toast';

const TemplateContext = createContext<TemplateContextValue | undefined>(undefined);

interface TemplateProviderProps {
  children: ReactNode;
}

/**
 * Template Provider
 * Manages the current template state and provides methods to change it
 */
export const TemplateProvider: React.FC<TemplateProviderProps> = ({ children }) => {
  const { tenantId, loading: tenantLoading } = useTenant();
  const { toast } = useToast();
  const [currentTemplate, setCurrentTemplate] = useState<TemplateId>('institutional');
  const [loading, setLoading] = useState(true);
  const availableTemplates = getAllTemplates();

  // Load the current template from the database
  useEffect(() => {
    const loadTemplate = async () => {
      // If the tenant context itself is still figuring out the tenant, wait.
      if (tenantLoading) return;

      if (!tenantId) {
        // Only stop loading if we are absolutely sure there is no tenant (not loading)
        // Note: The template context relies on TenantContext to manage its loading state appropriately.
        // If we reach here and we don't have a tenant, we stop loading.
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('site_template')
          .eq('id', tenantId)
          .single();

        if (error) {
          console.error('[TemplateContext] Error loading template:', error);
          return;
        }

        if (data?.site_template) {
          // Validate the template ID before setting it
          if (isValidTemplateId(data.site_template)) {
            console.log('[TemplateContext] Loading template:', data.site_template);
            setCurrentTemplate(data.site_template);
          } else {
            console.warn('[TemplateContext] Invalid template ID from database:', data.site_template);
            // Keep default 'institutional'
          }
        }
      } catch (error) {
        console.error('[TemplateContext] Error loading template:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();

    // Subscribe to changes in the tenants table
    const channel = supabase
      .channel('template-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenants',
          filter: `id=eq.${tenantId}`
        },
        (payload) => {
          console.log('[TemplateContext] Template updated:', payload);
          const newTemplate = (payload.new as any)?.site_template;
          if (newTemplate && isValidTemplateId(newTemplate)) {
            console.log('[TemplateContext] Switching to template:', newTemplate);
            setCurrentTemplate(newTemplate);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, tenantLoading]);

  /**
   * Change the active template
   * Saves the new template to the database
   */
  const setTemplate = async (templateId: TemplateId): Promise<void> => {
    if (!tenantId) {
      toast({
        title: 'Erro',
        description: 'Não foi possível identificar o tenant',
        variant: 'destructive'
      });
      return;
    }

    // Validate template ID
    if (!isValidTemplateId(templateId)) {
      toast({
        title: 'Erro',
        description: 'Template inválido',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('tenants')
        .update({ site_template: templateId })
        .eq('id', tenantId);

      if (error) {
        console.error('[TemplateContext] Error updating template:', error);
        toast({
          title: 'Erro',
          description: 'Não foi possível salvar o template',
          variant: 'destructive'
        });
        return;
      }

      // Update local state
      setCurrentTemplate(templateId);

      toast({
        title: 'Sucesso',
        description: 'Template atualizado com sucesso'
      });
    } catch (error) {
      console.error('[TemplateContext] Error updating template:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar template',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const value: TemplateContextValue = {
    currentTemplate,
    setTemplate,
    availableTemplates,
    loading
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};

/**
 * Hook to use the template context
 * Must be used within a TemplateProvider
 */
export const useTemplate = (): TemplateContextValue => {
  const context = useContext(TemplateContext);
  
  if (context === undefined) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  
  return context;
};
